import React, { createContext, useContext, useState, useEffect } from 'react'
import { backendGetRequest } from '../callBackend'
import checkFlag from '../featureFlags'

const ucsContext = createContext(null)

const rolesMap = {
  admin: 'is_admin',
  dashboardEditor: 'dashboard_editor',
}

/**
 * Context provider for UserCompanySettings
 *
 * @param {React.ReactNode} children child components that need access to UCS context
 * @returns {React.ReactNode} UCS context provider component
 *
 * @example
 * // ChildExampleComponent can use the useUcs hook or the PermissionGate to protect child components
 * <UserCompanySettingsContext>
 *   <ChildExampleComponent />
 * </UserCompanySettingsContext>
 */
export const UserCompanySettingsContext = ({ children }) => {
  const [ucs, setUcs] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState([])

  const getUcs = async () => {
    try {
      const ucsResp = await backendGetRequest('/api/user/user-company-settings')
      if (ucsResp.status !== 200) {
        throw new Error(`UCS GET error ${ucsResp.status}`)
      }
      setUcs(ucsResp.data?.data)
    } catch (error) {
      setError((prevState) => [...prevState, error])
      console.error('Failed to load user permissions:', error)
    } finally {
      setLoaded(true)
    }
  }

  useEffect(() => {
    getUcs()
  }, [])

  /**
   * Returns the UCS object pertaining to the current user's dashboard
   *
   * @returns {Object} UserCompanySettings object
   */
  const userPermissions = () => {
    return ucs.active_ucs
  }

  /**
   * Returns all UCS objects for the current user
   *
   * @returns {Array.<Object>} array of UserCompanySettings objects
   */
  const userPermissionsArray = () => {
    return ucs.all_ucs
  }

  /**
   * Gets user company settings display name
   *
   * @returns {String} formatted user name for display
   */
  const userName = () => {
    return ucs.active_ucs.user_display_name
  }

  /**
   * Gets user company settings email
   *
   * @returns {String} user's email
   */
  const userEmail = () => {
    return ucs.active_ucs.email
  }

  /**
   * Gets user company settings admin status
   *
   * @returns {Boolean} true if user is admin on dashboard
   */
  const isAdmin = () => {
    return Boolean(ucs.active_ucs.is_admin)
  }

  /**
   * Gets user company settings dashboard editor status
   *
   * @returns {Boolean} true if user is dashboard editor
   */
  const isDashboardEditor = () => {
    if (checkFlag('dashboardEditor')) {
      return Boolean(ucs?.active_ucs?.dashboard_editor)
    }

    return true
  }

  /**
   * Gets user payment request creation limit
   *
   * @returns {Number} max amount user can create payment requests for
   */
  const createLimit = () => {
    return ucs.active_ucs.transaction_creation_limit
  }

  /**
   * Gets user payment request approval limit
   *
   * @returns {Number} max amount for a payment request a user can approve
   */
  const approveLimit = () => {
    return ucs.active_ucs.transaction_approve_limit
  }

  /**
   * Gets user payment self approval limit
   *
   * @returns {Number} max amount for payments a user can approve that they created themselves
   */
  const selfApproveLimit = () => {
    return ucs.active_ucs.transaction_self_approve_limit
  }

  /**
   * Checks if the current user has a specific role.
   *
   * @param {string} role - The role identifier to check against the rolesMap
   * @returns {boolean|undefined} - Returns true if the user has the role, false if not
   * @throws {Error} - Throws an error if the specified role is not found in rolesMap
   */
  const isRole = (role) => {
    let userIsRole = false
    try {
      if (!role in rolesMap) {
        throw new Error(`role not recognized: ${role}`)
      }
      userIsRole = ucs.active_ucs[rolesMap[role]]
    } catch (error) {
      setError((prevState) => [...prevState, error])
      console.error('Failed to verify user role', error)
    } finally {
      return userIsRole
    }
  }

  /**
   * Checks user transaction limits with four return states
   * - return true of user limit >= amount
   * - return true if limit > 0 if amount is null
   * - return true if adminOverride is true and user is admin
   * - return true if user limit is < 0 (negative = no limit)
   *
   * @param {String} limit - the type of limit to check ('create', 'approve', or 'self-approve')
   * @param {Numer|null} amount - the transaction amount to check against the limit (optional)
   * @param {Boolean} adminOverride -
   * @returns {Boolean} - returns true if the amount is under the limit or if the limit is enabled
   * @throws {Error} - throws an error if the limit type is not recognized
   */
  const checkLimit = (limit, amount = null, adminOverride = true) => {
    let checkPasses = false
    try {
      if (adminOverride && ucs.active_ucs.is_admin) {
        checkPasses = true
        // empty return to jump to the 'finally' block
        return
      }
      let limitValue
      switch (limit) {
        case 'create':
          limitValue = parseFloat(ucs.active_ucs.transaction_creation_limit)
          break
        case 'approve':
          limitValue = parseFloat(ucs.active_ucs.transaction_approve_limit)
          break
        case 'self-approve':
          limitValue = parseFloat(ucs.active_ucs.transaction_self_approve_limit)
          break
        default:
          throw new Error(`limit not recognized: ${limit}`)
      }
      
      
      if (limitValue < 0) {
        checkPasses = true
        return
      }
      
      if (amount !== null) {
        amount = parseFloat(amount)
        checkPasses = limitValue >= amount
      } else {
        checkPasses = limitValue > 0
      }
    } catch (error) {
      setError((prevState) => [...prevState, error])
      console.error('Failed to verify user permission', error)
    } finally {
      return checkPasses
    }
  }
  
  const hasLimitedAccountAccess = () => {
    return Boolean(ucs?.active_ucs?.limited_access_account_tags)
  }

  return (
    <ucsContext.Provider
      value={{
        ucs,
        loaded,
        error,
        userPermissions,
        userPermissionsArray,
        userName,
        userEmail,
        isAdmin,
        isDashboardEditor,
        createLimit,
        approveLimit,
        selfApproveLimit,
        isRole,
        checkLimit,
        hasLimitedAccountAccess,
      }}
    >
      {children}
    </ucsContext.Provider>
  )
}

/**
 * ucsContext hook
 *
 * @returns {Context} current ucsContext object
 *
 * @example
 * // Checking that user has a create limit >= $5
 * const ucsContext = useUcs()
 * let userCanSendPayment = false
 * if (ucsContext.loaded) {
 *  userCanSendPayment = ucsContext.checkLimit('create', 5)
 * }
 */
export const useUcs = () => {
  const context = useContext(ucsContext)
  if (!context) {
    throw new Error('ucsUcs must be used within a UserCompanySettingsContext')
  }
  return context
}
